/* eslint-disable react-hooks/exhaustive-deps */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ListIcon from "@mui/icons-material/List";
import { Box, Button, Divider, Typography } from "@mui/material";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import { useLoadingOverlay } from "components/LoadingOverlay";
import ModalContainer from "components/ModalContainer";
import ModalLayout from "components/ModalLayout";
import SearchableInput from "components/SearchableInput";
import Table from "components/Table";
import TranslationButtons from "components/TranslationButtons";
import configData from "config/config.json";
import useOpenClose from "hooks/useOpenClose";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import {
  createLeagueAndMileStone,
  leagueInfo,
  updateLeagueAndMileStone,
} from "services/leagueServices";
import {
  packInfo as getPackInfo,
  getPlayerPacks as getRewardPacks,
} from "services/packServices";
import useStyles from "styles/league/leagueEditor";
import { leagueSchema } from "utils/validation";

const milestoneTemplate = {
  descText: "",
  descTextTrans: {},
  number: 1,
  steps: 1,
  rewardPack: null,
  rewardCoins: 0,
  rewardPackCount: 0
};

const initialValue = {
  name: "",
  number: 1,
  k1: 1,
  k2: 1,
  floorValue: null,
  ceilValue: null,
  milestones: [milestoneTemplate],
  kWeights: {} as Record<string, number>,
  kWeightsFe: [] as Array<[number, number]>,
};

const defaultPageSize = 20,
  defaultPageNo = 1;

const MileStoneEditor = ({
  id,
  index,
  onMilestoneChange,
  milestone,
  rewardOptions,
  onSearch,
  handleOpenModal,
}) => {
  const styles = useStyles();
  const selected = rewardOptions.find(
    (rewardOp) => rewardOp.id === milestone.rewardPack
  );

  const onChange = (e: any) => onMilestoneChange(e, index);

  return (
    <Box className={styles.milestoneRoot}>
      <Typography className={styles.milestoneHeaderText}>
        Milestone-{index + 1}
      </Typography>
      <Box className={styles.milestoneInputContainer}>
        <Box className={styles.largeField}>
          <SearchableInput
            selected={id !== "create" ? selected : milestone.rewardPack}
            name="rewardPack"
            options={rewardOptions}
            label="Reward Packs"
            placeholder="search packs"
            onInputChange={onSearch}
            onOptionSelect={(e) => onMilestoneChange(e, index)}
            customStyle={{ width: "100%" }}
          />
        </Box>
        <Box className={styles.smallField}>
          <SimpleTextInput
            type="number"
            label="Number *"
            name="number"
            value={milestone?.number}
            onChange={onChange}
          />
        </Box>
        <Box className={styles.smallField}>
          <SimpleTextInput
            type="number"
            label="Steps *"
            name="steps"
            value={milestone?.steps}
            onChange={onChange}
          />
        </Box>
        <Box className={styles.smallField}>
          <SimpleTextInput
            type="number"
            label="Reward Coins"
            name="rewardCoins"
            value={milestone?.rewardCoins}
            onChange={onChange}
          />
        </Box>
        <Box className={styles.smallField}>
          <SimpleTextInput
            type="number"
            label="Pack count *"
            name="rewardPackCount"
            value={milestone?.rewardPackCount}
            onChange={onChange}
          />
        </Box>
        <Box className={styles.field}>
          <SimpleTextInput
            label="Desc"
            name="descText"
            value={milestone?.descText}
            onChange={onChange}
            button={
              <TranslationButtons
                field={"descTextTrans"}
                onClick={(...props) => handleOpenModal(...props, index)}
              />
            }
          />
        </Box>
        {index !== 0 && (
          <DeleteIcon
            className={styles.delete}
            onClick={() => onChange({ target: { name: "delete" } })}
          />
        )}
      </Box>
      <Divider />
    </Box>
  );
};

const LeagueEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const [leagueDetails, setLeagueDetails] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [handleEvents, setHandleEvents] = useState({} as any);
  const [rewards, setRewards] = useState([]);
  const [options, setOptions] = useState({
    leagueOptions: Array.from({ length: 10 }, (v, k) => ({
      id: `${k + 1}`,
      label: `${k + 1}`,
    })),
    rewardOptions: [],
  });
  // eslint-disable-next-line no-unused-vars
  const [searchPagination, setSearchPagination] = useState({
    pageNo: defaultPageNo,
    pageSize: defaultPageSize,
    hasMore: false,
  });

  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });

  const { isOpen, extraData, open, close, updateExtra } = useOpenClose<{
    data?: { kValue?: string; kWeight?: string };
    index?: number;
    modalContentType?: string;
  }>();
  const {
    isOpen: isOpenLangModal,
    extraData: langExtraData,
    open: openLangModel,
    close: closeLangModal,
    updateExtra: updateLangExtraData,
  } = useOpenClose<{ milestone?: any }>();

  const generateOptions = (items: any) =>
    items.map((item, index) => ({
      id: item?._id,
      label: item?.idFromExcel
        ? `${item?.name}(${item?.idFromExcel})`
        : item?.name,
    }));

  const convertToPayload = (league: any) => {
    // modify payload here
    return {
      ...league,
      milestones:
        league?.milestones?.map((stone) => {
          return {
            ...stone,
            rewardPack: stone.rewardPack?.id || stone.rewardPack,
          };
        }) || [],
    };
  };

  const submit = async () => {
    showLoadingOverlay();
    const payload = convertToPayload(leagueDetails);
    try {
      let resp;

      await leagueSchema.validate(leagueDetails);

      const tempLeagueDetails = JSON.parse(JSON.stringify(leagueDetails));

      for (
        let index = 0;
        index < tempLeagueDetails.milestones.length;
        index++
      ) {
        const milestone = tempLeagueDetails.milestones[index];

        if (milestone.rewardPack) {
          tempLeagueDetails.milestones[index].rewardPack =
            tempLeagueDetails.milestones[index].rewardPack.id;
        }
      }

      if (id === "create") {
        resp = await createLeagueAndMileStone(payload);
      } else {
        resp = await updateLeagueAndMileStone(payload, id);
      }
      showSnackbar(resp.message, "success");
      hideLoadingOverlay();
      navigate(configData.routes.leagues.leagues);
    } catch (error) {
      console.log(error);

      if (error?.inner) {
        error.inner.forEach((error) => {
          showSnackbar(error.message, "error");
        });
      }

      hideLoadingOverlay();
      showSnackbar(error?.message);
    }
  };

  const cancel = () => navigate(configData.routes.leagues.leagues);

  const handleNumberFieldChange = ({ fieldName, value, _data }, flag) => {
    let data = { ..._data };
    const _value = parseInt(value) ?? null; //Number(value?.split(".")[0]) ?? "";
    let updatedData = {
      ...data,
      [fieldName]: _value,
    };

    if (flag) updatedData["name"] = `League_${value}`;

    return updatedData;
  };

  const onChange = (e, leagueFlag) => {
    const { name, value } = e?.target;
    let data = { ...leagueDetails };

    const eventHandler = handleEvents[name];

    if (eventHandler) {
      const resp = eventHandler(
        { fieldName: name, value, _data: data },
        leagueFlag
      );

      data = resp;
    } else {
      data = {
        ...data,
        [name]: value,
      };
    }

    setLeagueDetails(data);
  };

  const onMilestoneChange = (e, index) => {
    const { name, value } = e?.target;

    let milestones = [...leagueDetails.milestones];

    const eventHandler = handleEvents[name];

    if (name === "delete") {
      milestones = milestones.filter((_, idx) => idx !== index);
    } else if (eventHandler) {
      milestones = [
        ...leagueDetails.milestones?.slice(0, index),
        {
          ...eventHandler({
            fieldName: name,
            value,
            _data: leagueDetails.milestones[index],
          }),
        },
        ...leagueDetails.milestones?.slice(index + 1),
      ];
    } else {
      milestones = [
        ...leagueDetails.milestones?.slice(0, index),
        {
          ...leagueDetails.milestones[index],
          [name]: value,
        },
        ...leagueDetails.milestones?.slice(index + 1),
      ];
    }

    if (name === "descText") {
      milestones[index][`${name}Trans`] ??= {};
      value
        ? (milestones[index][`${name}Trans`]["en"] = value)
        : delete milestones[index][`${name}Trans`]["en"];
    }

    setLeagueDetails((_state) => ({ ..._state, milestones }));
    setOptions((st) => ({ ...st, rewardOptions: generateOptions(rewards) }));
  };

  const add = () => {
    setLeagueDetails((_state) => {
      let milestones = [..._state.milestones, milestoneTemplate];
      return { ..._state, milestones };
    });
  };

  const removeAll = () =>
    setLeagueDetails((_state) => ({
      ..._state,
      milestones: [milestoneTemplate],
    }));

  const fetchLeagueInfo = async () => {
    setLoading(true);
    try {
      const resp = await leagueInfo(id);
      setLeagueDetails(resp?.data);

      const rewardPacks = new Set(
        resp.data.milestones.map((el) => el.rewardPack)
      );

      let _requests = [];
      for (let reward of Array.from(rewardPacks)) {
        if (!options.rewardOptions.find((el) => el.reward == reward))
          _requests.push(getPackInfo(reward));
      }

      const packInfo = await Promise.all(_requests);
      setOptions((st) => ({
        ...st,
        rewardOptions: [
          st.rewardOptions,
          ...packInfo.map((el) => ({
            id: el?.data?._id,
            label: el?.data?.name,
          })),
        ],
      }));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const fetchRewardPacks = async (filters, init?) => {
    try {
      const resp = await getRewardPacks(filters);
      init && setRewards(resp?.items);
      setOptions((st) => ({
        ...st,
        rewardOptions: generateOptions(resp.items),
      }));
    } catch (error) {
      showSnackbar(error?.message || "Failed to fetch reward packs");
    }
  };

  const onSearchChange = async (searchValue) => {
    if (searchValue) {
      fetchRewardPacks({
        search: searchValue,
        skipPagination: true,
        ...searchPagination,
      });
    } else
      setOptions((st) => ({ ...st, rewardOptions: generateOptions(rewards) }));
  };

  const handleViewWeightage = () => {
    open();
    updateExtra({ data: null, modalContentType: "viewWeightage" });
  };

  const handleEditWeightage = ({ rowId, data, actionHandlers }: any) => {
    updateExtra({
      data: data,
      index: rowId,
      modalContentType: "editWeightage",
    });
  };

  const onWeightageChange = (e: any) => {
    const { name, value } = e?.target;
    updateExtra({ ...extraData, data: { ...extraData.data, [name]: value } });
  };

  const onWeightageSubmit = () => {
    close();
    setLeagueDetails((prevState) => {
      return {
        ...prevState,
        kWeights: {
          ...prevState.kWeights,
          [extraData.data.kValue]: parseInt(extraData.data.kWeight),
        },
        kWeightsFe: [
          ...prevState.kWeightsFe.slice(0, extraData.index),
          [parseInt(extraData.data.kValue), parseInt(extraData.data.kWeight)],
          ...prevState.kWeightsFe.slice(extraData.index + 1),
        ],
      };
    });
    open();
    updateExtra({ data: null, index: -1, modalContentType: "viewWeightage" });
  };

  const handleOpenModal = (type, field, milestone) => {
    updateLangExtraData({ milestone: milestone });
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field, milestone);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });

    openLangModel();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLangEditClick = (data: any) => {
    closeLangModal();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    openLangModel();
  };

  const handleLangDeleteClick = (data: any) => {
    let milestone = langExtraData?.milestone;

    if ([undefined, null].includes(milestone)) {
      showSnackbar("Invalid milestone selected", "error");
      return;
    }

    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error"
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setLeagueDetails((prev) => {
      prev.milestones[milestone][modalData.field] = Object.keys(
        prev.milestones[milestone][modalData.field]
      ).reduce((acc, key) => {
        if (key !== data.lang) {
          acc[key] = prev.milestones[milestone][modalData.field][key];
        }
        return acc;
      }, {});
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    let milestone = langExtraData?.milestone;

    if ([undefined, null].includes(milestone)) {
      showSnackbar("Invalid milestone selected", "error");
      return;
    }

    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setLeagueDetails((prev) => {
      const updatedMilestones = [...prev.milestones];
      const updatedField = { ...updatedMilestones[milestone][field] };

      if (modalData.inputValue) {
        updatedField[modalData.language] = modalData.inputValue;
      } else {
        delete updatedField[modalData.language];
      }

      updatedMilestones[milestone] = {
        ...updatedMilestones[milestone],
        [field]: updatedField,
      };

      return { ...prev, milestones: updatedMilestones };
    });

    // if english translation is available set it field
    if (modalData.language === "en") {
      setLeagueDetails((prev) => {
        const updatedMilestones = [...prev.milestones];
        updatedMilestones[milestone] = {
          ...updatedMilestones[milestone],
          descText: modalData.inputValue,
        };

        return { ...prev, milestones: updatedMilestones };
      });
    }
    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    closeLangModal();
  };

  const getTranslatedData = (field, milestone) => {
    const objEntries = Object.entries(
      leagueDetails.milestones[milestone][field] ?? {}
    );

    if (!objEntries?.length) return [];
    const _translatedData = objEntries?.map(([key, value]) => ({
      lang: key,
      inputValue: value,
    }));
    if (!_translatedData) return [];
    return _translatedData;
  };

  useEffect(() => {
    fetchRewardPacks(searchPagination, true);
    if (id !== "create") fetchLeagueInfo();
    const eventsMaps = {
      k1: handleNumberFieldChange,
      k2: handleNumberFieldChange,
      steps: handleNumberFieldChange,
      number: handleNumberFieldChange,
      floorValue: handleNumberFieldChange,
      ceilValue: handleNumberFieldChange,
      rewardCoins: handleNumberFieldChange,
      rewardPackCount: handleNumberFieldChange,
    };

    setHandleEvents(eventsMaps);
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={() => navigate(-1)}
      title={`${id === "create" ? "Create" : "Edit"} League & Milestones`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.formContainer}>
          <Box className={styles.field}>
            <SimpleSelectInput
              name="number"
              label="League Number"
              value={leagueDetails.number}
              options={options.leagueOptions}
              onChange={(e) => onChange(e, true)}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              type="text"
              label="League Name"
              name="name"
              value={leagueDetails.name}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              type="number"
              label="K1 *"
              name="k1"
              value={leagueDetails.k1}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              type="number"
              label="K2 *"
              name="k2"
              value={leagueDetails.k2}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              type="number"
              label="Floor Value"
              name="floorValue"
              value={leagueDetails.floorValue}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              type="number"
              label="Ceil Value"
              name="ceilValue"
              value={leagueDetails.ceilValue}
              onChange={onChange}
            />
          </Box>
          <Box style={{ maxWidth: 180, marginTop: 25 }}>
            <CustomButton
              startIcon={<ListIcon />}
              onClick={handleViewWeightage}
            >
              View Weightage
            </CustomButton>
          </Box>
        </Box>
        <Box className={styles.milestoneContainer}>
          <Box className={styles.milestoneButtonConatiner}>
            <Typography className={styles.milestoneText}>Milestones</Typography>
            <Box className={styles.buttonContainer}>
              <Button
                size="small"
                type="submit"
                variant="contained"
                onClick={add}
              >
                <AddIcon sx={{ color: 'white' }} />
                <span style={{ marginLeft: 5 }}>{"add"}</span>
              </Button>
              <Button
                size="small"
                type="submit"
                variant="contained"
                onClick={removeAll}
              >
                <DeleteForeverIcon sx={{ color: 'white' }} />
                <span style={{ marginLeft: 5 }}>{"delete"}</span>
              </Button>
            </Box>
          </Box>

          <Divider />
          <Box className={styles.milestoneFormContainer}>
            {leagueDetails?.milestones?.map((milestone, k) => {
              return (
                <MileStoneEditor
                  key={k}
                  id={id}
                  index={k}
                  milestone={milestone}
                  onSearch={onSearchChange}
                  rewardOptions={options.rewardOptions}
                  onMilestoneChange={onMilestoneChange}
                  handleOpenModal={handleOpenModal}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
      <ModalContainer openModel={isOpen} handleClose={close}>
        <ModalLayout isLargeContainer={false} hasPadding={true}>
          <Box className={styles.viewDataContainer}>
            <Typography style={{ fontWeight: "bold" }}>
              {(extraData.modalContentType === "editWeightage" ? "Edit " : "") +
                "League Weightages"}
            </Typography>
            {extraData.modalContentType === "viewWeightage" ? (
              <Table
                data={leagueDetails?.kWeightsFe?.map((el) => ({
                  kValue: el[0],
                  kWeight: el[1],
                }))}
                hidePagination={true}
                actions={(data) => (
                  <CustomButton onClick={() => handleEditWeightage(data)}>
                    Edit
                  </CustomButton>
                )}
                columns={[
                  {
                    id: "kValue",
                    label: "K Value",
                    alignHead: "center",
                    alignBody: "center",
                    isSortable: false,
                  },
                  {
                    id: "kWeight",
                    label: "K Weight",
                    alignHead: "center",
                    alignBody: "center",
                    isSortable: false,
                  },
                  {
                    id: "actions",
                    label: "Actions",
                  },
                ] as any}
              />
            ) : (
              <Box style={{ display: "flex", flexFlow: "column", gap: 10 }}>
                <Box style={{ display: "flex", gap: 5 }}>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      type="number"
                      label="K-Value"
                      name="kValue"
                      value={extraData?.data?.kValue}
                      onChange={onWeightageChange}
                    />
                  </Box>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      type="number"
                      label="K-Weight"
                      name="kWeight"
                      value={extraData?.data?.kWeight}
                      onChange={onWeightageChange}
                    />
                  </Box>
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  onClick={onWeightageSubmit}
                >
                  Save
                </Button>
              </Box>
            )}
          </Box>
        </ModalLayout>
      </ModalContainer>

      <ViewLanguageModal
        isMultiline
        label="Select Language"
        isOpen={isOpenLangModal}
        close={closeLangModal}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ] as any}
      />
    </ContentShell>
  );
};

export default LeagueEditor;
